import { useState, useEffect } from 'react';
import { notificationService } from '../../src/services/notification.service';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import {
  Calendar,
  FileText,
  Plus,
  Save,
  Clock,
  AlertTriangle,
  CloudRain,
  HardHat,
  Package,
  Upload,
  X,
  Edit2,
  Trash2,
  CheckCircle
} from 'lucide-react';
import {
  dailyLogService,
  DailyLogCreate,
  DailyLogUpdate,
  DailyLogActivity,
  DailyLogActivityCreate
} from '../../src/services/daily-log.service';
import { documentService } from '../../src/services/document.service';
import { scheduleService } from '../../src/services/schedule.service';
import { preConstructionService } from '../../src/services/pre-construction.service';
import { projectService } from '../../src/services/project.service';
import { authService } from '../../src/services/auth.service';
import { weatherService, WeatherData } from '../../src/services/weather.service';
import { userService } from '../../src/services/user.service';
import { useToast } from '../../hooks/use-toast';
import { useOrgCurrency } from '../../src/hooks/useOrgCurrency';

interface ActivityFormData {
  activity_id?: string;
  activity_title: string;
  activity_description: string;
  location_area: string;
  quantity_completed: string;
  unit_of_measure: string;
  percentage_complete: string;
  start_time: string;
  end_time: string;
  workers_assigned: string;
  hours_worked: string;
  equipment_used: string;
  boq_item_id: string;
  scheduled_task_id: string;
  is_completed: boolean;
  is_delayed: boolean;
  delay_reason: string;
  quality_notes: string;
  safety_notes: string;
  materials_consumed: string;
  material_waste_notes: string;
  notes: string;
  labour_cost: string;
  materials_cost: string;
  equipment_cost: string;
}

interface PhotoFile {
  file: File;
  preview: string;
  uploaded?: boolean;
  path?: string;
}

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: ActivityFormData) => void;
  activity?: ActivityFormData;
  scheduledTasks: any[];
  boqItems: any[];
  organizationId: string;
}

// Separate Activity Modal Component
function ActivityModal({ isOpen, onClose, onSave, activity, scheduledTasks, boqItems }: ActivityModalProps) {
  const { toast } = useToast();
  const orgCurrency = useOrgCurrency();
  const [formData, setFormData] = useState<ActivityFormData>(
    activity || {
      activity_title: '',
      activity_description: '',
      location_area: '',
      quantity_completed: '',
      unit_of_measure: 'cubic meters',
      percentage_complete: '',
      start_time: '',
      end_time: '',
      workers_assigned: '',
      hours_worked: '',
      equipment_used: '',
      boq_item_id: '',
      scheduled_task_id: '',
      is_completed: false,
      is_delayed: false,
      delay_reason: '',
      quality_notes: '',
      safety_notes: '',
      materials_consumed: '',
      material_waste_notes: '',
      notes: '',
      labour_cost: '',
      materials_cost: '',
      equipment_cost: ''
    }
  );

  useEffect(() => {
    if (activity) {
      setFormData(activity);
    }
  }, [activity]);

  // Auto-calculate hours worked when start_time or end_time changes
  useEffect(() => {
    if (formData.start_time && formData.end_time) {
      const [startHour, startMin] = formData.start_time.split(':').map(Number);
      const [endHour, endMin] = formData.end_time.split(':').map(Number);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      let diffMinutes = endMinutes - startMinutes;

      // Handle crossing midnight
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60;
      }

      const hours = (diffMinutes / 60).toFixed(2);
      setFormData(prev => ({ ...prev, hours_worked: hours }));
    }
  }, [formData.start_time, formData.end_time]);

  const handleSave = () => {
    if (!formData.activity_title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Activity title is required',
        variant: 'error'
      });
      return;
    }

    if (!formData.activity_description.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Activity description is required',
        variant: 'error'
      });
      return;
    }

    const labourCost = parseFloat(formData.labour_cost);
    const materialsCost = parseFloat(formData.materials_cost);
    const equipmentCost = parseFloat(formData.equipment_cost);

    if (isNaN(labourCost) || labourCost < 0) {
      toast({ title: 'Validation Error', description: 'Labour cost is required', variant: 'error' });
      return;
    }
    if (isNaN(materialsCost) || materialsCost < 0) {
      toast({ title: 'Validation Error', description: 'Materials cost is required', variant: 'error' });
      return;
    }
    if (isNaN(equipmentCost) || equipmentCost < 0) {
      toast({ title: 'Validation Error', description: 'Equipment cost is required', variant: 'error' });
      return;
    }

    onSave(formData);
    toast({
      title: 'Success',
      description: activity ? 'Activity updated successfully' : 'Activity added successfully',
      variant: 'success'
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto !border-[3px] !border-blue-500 !shadow-[0_0_30px_rgba(59,130,246,0.5)]"
        style={{
          background: 'linear-gradient(135deg, #dbeafe 0%, #ffffff 50%, #dbeafe 100%)',
          boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.3), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 !text-blue-700">
            <div className="p-1.5 bg-blue-100 rounded-full">
              <Clock className="h-4 w-4 text-blue-600" />
            </div>
            <span>{activity ? 'Edit Activity' : 'Add Activity'}</span>
          </DialogTitle>
          <DialogDescription className="!text-blue-600">
            {activity ? 'Update activity details' : 'Add a new activity to this daily log'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Activity Title */}
          <div className="space-y-2">
            <Label htmlFor="act-title">Activity Title *</Label>
            <Input
              id="act-title"
              value={formData.activity_title}
              onChange={(e) => setFormData({ ...formData, activity_title: e.target.value })}
              placeholder="E.g., Column Concrete Pour C1-C4"
            />
          </div>

          {/* Activity Description */}
          <div className="space-y-2">
            <Label htmlFor="act-description">Activity Description *</Label>
            <Textarea
              id="act-description"
              value={formData.activity_description}
              onChange={(e) => setFormData({ ...formData, activity_description: e.target.value })}
              placeholder="E.g., Concrete pouring for columns C1-C4"
              rows={2}
              className="resize-none"
            />
          </div>

          {/* Location and Task */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="act-location">Location/Area</Label>
              <Input
                id="act-location"
                value={formData.location_area}
                onChange={(e) => setFormData({ ...formData, location_area: e.target.value })}
                placeholder="North Wing, Grid A-B"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-task">Scheduled Task</Label>
              <Select value={formData.scheduled_task_id} onValueChange={(value) => setFormData({ ...formData, scheduled_task_id: value })}>
                <SelectTrigger id="act-task">
                  <SelectValue placeholder="Select task (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {scheduledTasks.filter((task: any) => task.task_id).map((task: any) => (
                    <SelectItem key={task.task_id} value={task.task_id}>
                      {task.task_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quantity, Unit, Progress */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="act-qty">Quantity</Label>
              <Input
                id="act-qty"
                type="number"
                value={formData.quantity_completed}
                onChange={(e) => setFormData({ ...formData, quantity_completed: e.target.value })}
                placeholder="15.5"
                step="0.1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-unit">Unit</Label>
              <Select value={formData.unit_of_measure} onValueChange={(value) => setFormData({ ...formData, unit_of_measure: value })}>
                <SelectTrigger id="act-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cubic meters">m³</SelectItem>
                  <SelectItem value="square meters">m²</SelectItem>
                  <SelectItem value="linear meters">m</SelectItem>
                  <SelectItem value="each">Each</SelectItem>
                  <SelectItem value="tons">Tons</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-pct">% Complete</Label>
              <Input
                id="act-pct"
                type="number"
                value={formData.percentage_complete}
                onChange={(e) => setFormData({ ...formData, percentage_complete: e.target.value })}
                placeholder="100"
                min="0"
                max="100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-workers">Workers</Label>
              <Input
                id="act-workers"
                type="number"
                value={formData.workers_assigned}
                onChange={(e) => setFormData({ ...formData, workers_assigned: e.target.value })}
                placeholder="6"
                min="0"
              />
            </div>
          </div>

          {/* Time Tracking */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="act-start">Start Time</Label>
              <Input
                id="act-start"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-end">End Time</Label>
              <Input
                id="act-end"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-hours">Hours Worked (auto)</Label>
              <Input
                id="act-hours"
                type="number"
                value={formData.hours_worked}
                onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                placeholder="Auto-calculated"
                step="0.25"
                readOnly
                className="bg-muted cursor-not-allowed"
              />
            </div>
          </div>

          {/* Equipment Used */}
          <div className="space-y-2">
            <Label htmlFor="act-equip">Equipment Used</Label>
            <Input
              id="act-equip"
              value={formData.equipment_used}
              onChange={(e) => setFormData({ ...formData, equipment_used: e.target.value })}
              placeholder="Pump #1, Crane"
            />
          </div>

          {/* Activity Cost Breakdown */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">
              Activity Cost ({orgCurrency}) *
              <span className="font-normal text-muted-foreground ml-1">— cumulative actual expenditure for this activity</span>
            </Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="act-labour-cost" className="text-xs text-muted-foreground">Labour *</Label>
                <Input
                  id="act-labour-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.labour_cost}
                  onChange={(e) => setFormData({ ...formData, labour_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="act-materials-cost" className="text-xs text-muted-foreground">Materials *</Label>
                <Input
                  id="act-materials-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.materials_cost}
                  onChange={(e) => setFormData({ ...formData, materials_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="act-equipment-cost" className="text-xs text-muted-foreground">Equipment *</Label>
                <Input
                  id="act-equipment-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.equipment_cost}
                  onChange={(e) => setFormData({ ...formData, equipment_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            {(formData.labour_cost || formData.materials_cost || formData.equipment_cost) && (
              <p className="text-xs text-muted-foreground">
                Total: {orgCurrency} {(
                  (parseFloat(formData.labour_cost) || 0) +
                  (parseFloat(formData.materials_cost) || 0) +
                  (parseFloat(formData.equipment_cost) || 0)
                ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Status Checkboxes */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="act-completed"
                checked={formData.is_completed}
                onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="act-completed" className="cursor-pointer font-normal">
                Activity completed
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="act-delayed"
                checked={formData.is_delayed}
                onChange={(e) => setFormData({ ...formData, is_delayed: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="act-delayed" className="cursor-pointer font-normal">
                Activity delayed
              </Label>
            </div>
          </div>

          {/* Delay Reason (if delayed) */}
          {formData.is_delayed && (
            <div className="space-y-2">
              <Label htmlFor="act-delay">Delay Reason</Label>
              <Textarea
                id="act-delay"
                value={formData.delay_reason}
                onChange={(e) => setFormData({ ...formData, delay_reason: e.target.value })}
                placeholder="Explain why the activity was delayed..."
                rows={2}
                className="resize-none"
              />
            </div>
          )}

          {/* Materials and Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="act-materials">Materials Consumed</Label>
              <Textarea
                id="act-materials"
                value={formData.materials_consumed}
                onChange={(e) => setFormData({ ...formData, materials_consumed: e.target.value })}
                placeholder="15.5 m³ Grade 30 concrete..."
                rows={2}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-waste">Material Waste Notes</Label>
              <Textarea
                id="act-waste"
                value={formData.material_waste_notes}
                onChange={(e) => setFormData({ ...formData, material_waste_notes: e.target.value })}
                placeholder="Minimal waste..."
                rows={2}
                className="resize-none"
              />
            </div>
          </div>

          {/* Quality and Safety Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="act-quality">Quality Notes</Label>
              <Textarea
                id="act-quality"
                value={formData.quality_notes}
                onChange={(e) => setFormData({ ...formData, quality_notes: e.target.value })}
                placeholder="Slump test passed..."
                rows={2}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="act-safety">Safety Notes</Label>
              <Textarea
                id="act-safety"
                value={formData.safety_notes}
                onChange={(e) => setFormData({ ...formData, safety_notes: e.target.value })}
                placeholder="All workers wearing PPE..."
                rows={2}
                className="resize-none"
              />
            </div>
          </div>

          {/* General Notes */}
          <div className="space-y-2">
            <Label htmlFor="act-notes">Additional Notes</Label>
            <Textarea
              id="act-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional observations..."
              rows={2}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            <Save className="h-4 w-4 mr-2" />
            {activity ? 'Update' : 'Add'} Activity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DailyLogFormResponsiveProps {
  projectId?: string;
  dailyLogId?: string;
  mode?: 'create' | 'edit';
  onSuccess?: () => void;
  onCancel?: () => void;
  trigger?: React.ReactNode;
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DailyLogFormResponsive({
  projectId,
  dailyLogId,
  mode = 'create',
  onSuccess,
  onCancel,
  trigger,
  externalOpen,
  onOpenChange,
}: DailyLogFormResponsiveProps) {
  const { toast } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (val: boolean) => {
    if (onOpenChange) onOpenChange(val);
    else setInternalOpen(val);
  };
  const [isLoading, setIsLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState('');

  // Activity modal state
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [editingActivityIndex, setEditingActivityIndex] = useState<number | null>(null);
  const [activities, setActivities] = useState<ActivityFormData[]>([]);

  // Photos state
  const [photos, setPhotos] = useState<PhotoFile[]>([]);

  // Weather state
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const [weatherAutoFill, setWeatherAutoFill] = useState(false);

  // Reference data
  const [projects, setProjects] = useState<any[]>([]);
  const [scheduledTasks, setScheduledTasks] = useState<any[]>([]);
  const [boqItems, setBoqItems] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    log_date: new Date().toISOString().split('T')[0],
    project_id: projectId || '',
    log_number: '',
    log_title: '',
    weather_conditions: '',
    temperature_high: '',
    temperature_low: '',
    wind_conditions: '',
    site_conditions: '',
    work_performed: '',
    work_completed_percentage: '',
    areas_worked: '',
    scheduled_activities: '',
    completed_activities: '',
    delayed_activities: '',
    reason_for_delays: '',
    total_workers_on_site: '',
    contractors_on_site: '',
    foreman: '',
    equipment_on_site: '',
    equipment_issues: '',
    materials_delivered: '',
    materials_used: '',
    material_shortages: '',
    safety_incidents: '',
    safety_meetings_held: false,
    quality_issues: '',
    inspections_conducted: '',
    visitors_on_site: '',
    meetings_held: '',
    problems_encountered: '',
    corrective_actions: '',
    follow_up_required: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen) return;

      try {
        setIsLoading(true);

        // Get organization ID
        const userProfile = await authService.getUserProfile();
        if (userProfile.organizationId) {
          setOrganizationId(userProfile.organizationId);

          // Load projects for the organization
          try {
            const projectsResponse = await projectService.getProjects(1, 100, {
              organization_id: userProfile.organizationId
            });
            if (projectsResponse.success && projectsResponse.data) {
              // Handle both response formats: data.data (paginated) or just data (array)
              const projectsArray = Array.isArray(projectsResponse.data)
                ? projectsResponse.data
                : (projectsResponse.data as any).data;
              setProjects(projectsArray || []);
            }
          } catch (error) {
            console.error('Error loading projects:', error);
          }

          // Load users for foreman dropdown
          try {
            const usersResponse = await userService.getUsers(1, 200);
            if (usersResponse.success && usersResponse.data) {
              const usersArray = Array.isArray(usersResponse.data)
                ? usersResponse.data
                : (usersResponse.data as any).data;
              setUsers(usersArray || []);
            }
          } catch (error) {
            console.error('Error loading users:', error);
          }
        }

        // Load existing log if editing
        if (mode === 'edit' && dailyLogId) {
          const response = await dailyLogService.getDailyLog(dailyLogId);
          if (response.data) {
            const log = response.data;
            setFormData({
              log_date: log.log_date,
              project_id: log.project_id,
              log_number: log.log_number,
              log_title: (log as any).log_title || '',
              weather_conditions: log.weather_conditions || '',
              temperature_high: log.temperature_high?.toString() || '',
              temperature_low: log.temperature_low?.toString() || '',
              wind_conditions: log.wind_conditions || '',
              site_conditions: log.site_conditions || '',
              work_performed: log.work_performed || '',
              work_completed_percentage: log.work_completed_percentage?.toString() || '',
              areas_worked: log.areas_worked || '',
              scheduled_activities: log.scheduled_activities || '',
              completed_activities: log.completed_activities || '',
              delayed_activities: log.delayed_activities || '',
              reason_for_delays: log.reason_for_delays || '',
              total_workers_on_site: log.total_workers_on_site?.toString() || '',
              contractors_on_site: log.contractors_on_site || '',
              foreman: (log as any).foreman || '',
              equipment_on_site: log.equipment_on_site || '',
              equipment_issues: log.equipment_issues || '',
              materials_delivered: log.materials_delivered || '',
              materials_used: log.materials_used || '',
              material_shortages: log.material_shortages || '',
              safety_incidents: log.safety_incidents || '',
              safety_meetings_held: log.safety_meetings_held || false,
              quality_issues: log.quality_issues || '',
              inspections_conducted: log.inspections_conducted || '',
              visitors_on_site: log.visitors_on_site || '',
              meetings_held: log.meetings_held || '',
              problems_encountered: log.problems_encountered || '',
              corrective_actions: log.corrective_actions || '',
              follow_up_required: log.follow_up_required || ''
            });

            // Load activities
            const activitiesResponse = await dailyLogService.getDailyLogActivities(dailyLogId);
            if (activitiesResponse.data) {
              setActivities(
                activitiesResponse.data.map((a: DailyLogActivity) => ({
                  activity_id: a.activity_id,
                  activity_title: a.activity_title || '',
                  activity_description: a.activity_description,
                  location_area: a.location_area || '',
                  quantity_completed: a.quantity_completed?.toString() || '',
                  unit_of_measure: a.unit_of_measure || 'cubic meters',
                  percentage_complete: a.percentage_complete?.toString() || '',
                  start_time: a.start_time || '',
                  end_time: a.end_time || '',
                  workers_assigned: a.workers_assigned?.toString() || '',
                  hours_worked: a.hours_worked?.toString() || '',
                  equipment_used: a.equipment_used || '',
                  boq_item_id: a.boq_item_id || '',
                  scheduled_task_id: a.scheduled_task_id || '',
                  is_completed: a.is_completed || false,
                  is_delayed: a.is_delayed || false,
                  delay_reason: a.delay_reason || '',
                  quality_notes: a.quality_notes || '',
                  safety_notes: a.safety_notes || '',
                  materials_consumed: a.materials_consumed || '',
                  material_waste_notes: a.material_waste_notes || '',
                  notes: a.notes || '',
                  labour_cost: a.labour_cost?.toString() || '',
                  materials_cost: a.materials_cost?.toString() || '',
                  equipment_cost: a.equipment_cost?.toString() || ''
                }))
              );
            }
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load data',
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isOpen, mode, dailyLogId]);

  // Auto-generate log number
  useEffect(() => {
    if (mode === 'create' && formData.log_date && !formData.log_number) {
      const logNumber = `DL-${formData.log_date}-001`;
      setFormData((prev) => ({ ...prev, log_number: logNumber }));
    }
  }, [formData.log_date, mode]);

  // Load tasks and BOQ when project changes
  useEffect(() => {
    const loadProjectData = async () => {
      if (!formData.project_id) {
        setScheduledTasks([]);
        setBoqItems([]);
        return;
      }

      if (!organizationId) {
        return;
      }

      try {
        // Load scheduled tasks
        const schedulesResponse = await scheduleService.getSchedules(1, 100, {
          project_id: formData.project_id
        }, organizationId);

        // Handle both response formats: direct array or data.data
        const schedulesArray = Array.isArray(schedulesResponse.data)
          ? schedulesResponse.data
          : schedulesResponse.data?.data;

        if (schedulesArray && schedulesArray.length > 0) {
          const schedule = schedulesArray[0];
          const tasksResponse = await scheduleService.getScheduleTasks(schedule.schedule_id);

          if (tasksResponse.data) {
            const tasksArray = Array.isArray(tasksResponse.data)
              ? tasksResponse.data
              : (tasksResponse.data as any)?.data || [];
            setScheduledTasks(tasksArray);
          } else {
            setScheduledTasks([]);
          }
        } else {
          setScheduledTasks([]);
        }

        // Load BOQ items
        const boqResponse = await preConstructionService.getBOQs(formData.project_id, 1, 100);
        if (boqResponse.data?.data && boqResponse.data.data.length > 0) {
          const boq = boqResponse.data.data[0];
          const itemsResponse = await preConstructionService.getBOQItems(boq.boq_id);
          if (itemsResponse.data) {
            setBoqItems(itemsResponse.data);
          }
        } else {
          setBoqItems([]);
        }
      } catch (error) {
        console.error('Error loading project data:', error);
        setScheduledTasks([]);
        setBoqItems([]);
      }
    };

    loadProjectData();
  }, [formData.project_id, organizationId]);

  // Photo handling
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newPhotos: PhotoFile[] = Array.from(e.target.files).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        uploaded: false
      }));
      setPhotos([...photos, ...newPhotos]);
    }
  };

  const removePhoto = (index: number) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  // Fetch weather data
  const handleFetchWeather = async () => {
    setIsLoadingWeather(true);
    try {
      const weatherData = await weatherService.getWeatherForCurrentLocation();

      if (weatherData) {
        setFormData({
          ...formData,
          weather_conditions: weatherData.conditions,
          temperature_high: weatherData.temperature_high?.toString() || weatherData.temperature.toString(),
          temperature_low: weatherData.temperature_low?.toString() || '',
          wind_conditions: weatherService.formatWindConditions(weatherData),
          site_conditions: weatherService.getSiteConditionsSuggestion(weatherData)
        });

        toast({
          title: 'Weather loaded',
          description: `Current weather: ${weatherData.conditions}, ${weatherData.temperature}°C`
        });
      } else {
        toast({
          title: 'Weather unavailable',
          description: 'Could not fetch weather data. Please enter manually.',
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch weather data',
        variant: 'error'
      });
    } finally {
      setIsLoadingWeather(false);
    }
  };

  const uploadPhotos = async (): Promise<string> => {
    if (photos.length === 0) return '';

    const date = formData.log_date;
    const basePath = `/daily-logs/${formData.project_id}/${date}`;

    for (const photo of photos) {
      if (!photo.uploaded) {
        try {
          await documentService.uploadAndCreateDocument(photo.file, {
            organization_id: organizationId,
            project_id: formData.project_id,
            title: `Daily Log Photo - ${date}`,
            document_number: `DL-${date}-${Date.now()}`,
            category: 'project_documents',
            document_type: 'photo',
            description: `Photo from daily log ${formData.log_number}`,
            file_name: photo.file.name,
            file_format: photo.file.type,
            file_size_bytes: photo.file.size,
            version: '1.0',
            confidentiality_level: 'internal',
            is_confidential: false,
            status: 'Approved'
          }, organizationId);
          photo.uploaded = true;
        } catch (error) {
          console.error('Error uploading photo:', error);
        }
      }
    }

    return basePath;
  };

  // Activity handling
  const handleSaveActivity = (activity: ActivityFormData) => {
    if (editingActivityIndex !== null) {
      const newActivities = [...activities];
      newActivities[editingActivityIndex] = activity;
      setActivities(newActivities);
    } else {
      setActivities([...activities, activity]);
    }
    setEditingActivityIndex(null);
  };

  const handleEditActivity = (index: number) => {
    setEditingActivityIndex(index);
    setIsActivityModalOpen(true);
  };

  const handleDeleteActivity = (index: number) => {
    const newActivities = [...activities];
    newActivities.splice(index, 1);
    setActivities(newActivities);
  };

  const resetForm = () => {
    setFormData({
      log_date: new Date().toISOString().split('T')[0],
      project_id: projectId || '',
      log_number: '',
      log_title: '',
      weather_conditions: '',
      temperature_high: '',
      temperature_low: '',
      wind_conditions: '',
      site_conditions: '',
      work_performed: '',
      work_completed_percentage: '',
      areas_worked: '',
      scheduled_activities: '',
      completed_activities: '',
      delayed_activities: '',
      reason_for_delays: '',
      total_workers_on_site: '',
      contractors_on_site: '',
      foreman: '',
      equipment_on_site: '',
      equipment_issues: '',
      materials_delivered: '',
      materials_used: '',
      material_shortages: '',
      safety_incidents: '',
      safety_meetings_held: false,
      quality_issues: '',
      inspections_conducted: '',
      visitors_on_site: '',
      meetings_held: '',
      problems_encountered: '',
      corrective_actions: '',
      follow_up_required: ''
    });
    setActivities([]);
    setPhotos([]);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.log_date) newErrors.log_date = 'Date is required';
    if (!formData.project_id) newErrors.project_id = 'Project is required';
    if (!formData.log_number) newErrors.log_number = 'Log number is required';
    if (!formData.log_title) newErrors.log_title = 'Log title is required';
    if (mode === 'create' && activities.length === 0) newErrors.activities = 'At least one activity is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !organizationId) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'error'
      });
      return;
    }

    setIsLoading(true);

    try {
      const photoPath = await uploadPhotos();

      const logPayload = {
        log_title: formData.log_title || undefined,
        weather_conditions: formData.weather_conditions || undefined,
        temperature_high: formData.temperature_high ? parseFloat(formData.temperature_high) : undefined,
        temperature_low: formData.temperature_low ? parseFloat(formData.temperature_low) : undefined,
        wind_conditions: formData.wind_conditions || undefined,
        site_conditions: formData.site_conditions || undefined,
        work_performed: formData.work_performed || undefined,
        work_completed_percentage: formData.work_completed_percentage
          ? parseFloat(formData.work_completed_percentage)
          : undefined,
        areas_worked: formData.areas_worked || undefined,
        scheduled_activities: formData.scheduled_activities || undefined,
        completed_activities: formData.completed_activities || undefined,
        delayed_activities: formData.delayed_activities || undefined,
        reason_for_delays: formData.reason_for_delays || undefined,
        total_workers_on_site: formData.total_workers_on_site ? parseInt(formData.total_workers_on_site) : undefined,
        contractors_on_site: formData.contractors_on_site || undefined,
        equipment_on_site: formData.equipment_on_site || undefined,
        equipment_issues: formData.equipment_issues || undefined,
        materials_delivered: formData.materials_delivered || undefined,
        materials_used: formData.materials_used || undefined,
        material_shortages: formData.material_shortages || undefined,
        safety_incidents: formData.safety_incidents || null,
        safety_meetings_held: formData.safety_meetings_held,
        quality_issues: formData.quality_issues || undefined,
        inspections_conducted: formData.inspections_conducted || undefined,
        visitors_on_site: formData.visitors_on_site || undefined,
        meetings_held: formData.meetings_held || undefined,
        problems_encountered: formData.problems_encountered || undefined,
        corrective_actions: formData.corrective_actions || undefined,
        follow_up_required: formData.follow_up_required || undefined,
        photos_taken: photos.length,
        photo_storage_path: photoPath || undefined
      };

      let logId = dailyLogId;

      if (mode === 'create') {
        const createData: DailyLogCreate = {
          project_id: formData.project_id,
          organization_id: organizationId,
          log_date: formData.log_date,
          log_number: formData.log_number,
          ...logPayload
        };

        const response = await dailyLogService.createDailyLog(createData);
        logId = response.data?.daily_log_id;

        const project = projects.find((p: any) => p.project_id === formData.project_id);
        if (project?.project_manager_id) {
          notificationService.dailyLogCreated(
            project.project_manager_id,
            project.project_name ?? formData.project_id,
            formData.log_date,
          );
        }

        toast({
          title: 'Success',
          description: 'Daily log created successfully',
          variant: 'success'
        });
      } else {
        if (!dailyLogId) throw new Error('Daily log ID required');

        await dailyLogService.updateDailyLog(dailyLogId, logPayload);

        toast({
          title: 'Success',
          description: 'Daily log updated successfully',
          variant: 'success'
        });
      }

      // Save activities
      if (logId && activities.length > 0) {
        for (const activity of activities) {
          const activityPayload: DailyLogActivityCreate = {
            organization_id: organizationId,
            activity_title: activity.activity_title,
            activity_description: activity.activity_description,
            location_area: activity.location_area || undefined,
            quantity_completed: activity.quantity_completed ? parseFloat(activity.quantity_completed) : undefined,
            unit_of_measure: activity.unit_of_measure || undefined,
            percentage_complete: activity.percentage_complete ? parseFloat(activity.percentage_complete) : undefined,
            start_time: activity.start_time || undefined,
            end_time: activity.end_time || undefined,
            workers_assigned: activity.workers_assigned ? parseInt(activity.workers_assigned) : undefined,
            hours_worked: activity.hours_worked ? parseFloat(activity.hours_worked) : undefined,
            equipment_used: activity.equipment_used || undefined,
            boq_item_id: activity.boq_item_id || undefined,
            scheduled_task_id: activity.scheduled_task_id || undefined,
            labour_cost: activity.labour_cost ? parseFloat(activity.labour_cost) : undefined,
            materials_cost: activity.materials_cost ? parseFloat(activity.materials_cost) : undefined,
            equipment_cost: activity.equipment_cost ? parseFloat(activity.equipment_cost) : undefined,
            is_completed: activity.is_completed,
            is_delayed: activity.is_delayed,
            delay_reason: activity.delay_reason || null,
            quality_notes: activity.quality_notes || undefined,
            safety_notes: activity.safety_notes || undefined,
            materials_consumed: activity.materials_consumed || undefined,
            material_waste_notes: activity.material_waste_notes || undefined,
            notes: activity.notes || undefined
          };

          if (activity.activity_id && mode === 'edit') {
            await dailyLogService.updateDailyLogActivity(activity.activity_id, activityPayload);
          } else {
            await dailyLogService.createDailyLogActivity(logId, activityPayload);
          }
        }
      }

      // Clear form on successful creation
      if (mode === 'create') {
        resetForm();
      }

      setIsOpen(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Error saving daily log:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save daily log',
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Daily Log
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {mode === 'create' ? 'Create Daily Log' : 'Edit Daily Log'}
            </DialogTitle>
            <DialogDescription>
              Record daily construction activities, progress, and site conditions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="log-date">Date *</Label>
                  <Input
                    id="log-date"
                    type="date"
                    value={formData.log_date}
                    onChange={(e) => setFormData({ ...formData, log_date: e.target.value })}
                    disabled={mode === 'edit'}
                  />
                  {errors.log_date && <p className="text-sm text-destructive">{errors.log_date}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project">Project *</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    disabled={mode === 'edit' || !!projectId}
                  >
                    <SelectTrigger id="project">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.filter((project) => project.project_id).map((project) => (
                        <SelectItem key={project.project_id} value={project.project_id}>
                          {project.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.project_id && <p className="text-sm text-destructive">{errors.project_id}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="log-title">Log Title *</Label>
                <Input
                  id="log-title"
                  value={formData.log_title}
                  onChange={(e) => setFormData({ ...formData, log_title: e.target.value })}
                  placeholder="Daily progress on foundation work"
                />
                {errors.log_title && <p className="text-sm text-destructive">{errors.log_title}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="log-number">Log Number *</Label>
                  <Input
                    id="log-number"
                    value={formData.log_number}
                    onChange={(e) => setFormData({ ...formData, log_number: e.target.value })}
                    disabled={mode === 'edit'}
                  />
                  {errors.log_number && <p className="text-sm text-destructive">{errors.log_number}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="progress">Progress %</Label>
                  <Input
                    id="progress"
                    type="number"
                    value={formData.work_completed_percentage}
                    onChange={(e) => setFormData({ ...formData, work_completed_percentage: e.target.value })}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              </div>
            </div>

            {/* Weather */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <CloudRain className="h-4 w-4" />
                  Weather & Site Conditions
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleFetchWeather}
                  disabled={isLoadingWeather}
                  className="text-xs"
                >
                  {isLoadingWeather ? (
                    <>
                      <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full mr-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CloudRain className="h-3 w-3 mr-2" />
                      Auto-fill Weather
                    </>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Weather</Label>
                  <Select
                    value={formData.weather_conditions}
                    onValueChange={(value) => setFormData({ ...formData, weather_conditions: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Clear">Clear</SelectItem>
                      <SelectItem value="Partly Cloudy">Partly Cloudy</SelectItem>
                      <SelectItem value="Cloudy">Cloudy</SelectItem>
                      <SelectItem value="Light Rain">Light Rain</SelectItem>
                      <SelectItem value="Heavy Rain">Heavy Rain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Site Conditions</Label>
                  <Input
                    value={formData.site_conditions}
                    onChange={(e) => setFormData({ ...formData, site_conditions: e.target.value })}
                    placeholder="Dry, good conditions"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Temp High (°C)</Label>
                  <Input
                    type="number"
                    value={formData.temperature_high}
                    onChange={(e) => setFormData({ ...formData, temperature_high: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temp Low (°C)</Label>
                  <Input
                    type="number"
                    value={formData.temperature_low}
                    onChange={(e) => setFormData({ ...formData, temperature_low: e.target.value })}
                  />
                </div>
                <div className="space-y-2 sm:col-span-1 col-span-2">
                  <Label>Wind</Label>
                  <Input
                    value={formData.wind_conditions}
                    onChange={(e) => setFormData({ ...formData, wind_conditions: e.target.value })}
                    placeholder="Light breeze"
                  />
                </div>
              </div>
            </div>

            {/* Work Performed */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <HardHat className="h-4 w-4" />
                Work & Crew
              </h3>
              <div className="space-y-2">
                <Label>Work Performed</Label>
                <Textarea
                  value={formData.work_performed}
                  onChange={(e) => setFormData({ ...formData, work_performed: e.target.value })}
                  placeholder="Foundation work continued..."
                  rows={3}
                  className="resize-none"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Foreman</Label>
                  <Select
                    value={formData.foreman}
                    onValueChange={(value) => setFormData({ ...formData, foreman: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select foreman" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.length === 0 ? (
                        <SelectItem value="loading" disabled>Loading users...</SelectItem>
                      ) : (
                        users.filter((user) => user.user_id || user.id).map((user) => {
                          const userId = user.user_id || user.id;
                          const userName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;

                          return (
                            <SelectItem key={userId} value={userId}>
                              {userName}
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Total Workers</Label>
                  <Input
                    type="number"
                    value={formData.total_workers_on_site}
                    onChange={(e) => setFormData({ ...formData, total_workers_on_site: e.target.value })}
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contractors</Label>
                  <Input
                    value={formData.contractors_on_site}
                    onChange={(e) => setFormData({ ...formData, contractors_on_site: e.target.value })}
                    placeholder="ABC Concrete (8 workers)..."
                  />
                </div>
              </div>
            </div>

            {/* Activities Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Activities ({activities.length})
                </h3>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    if (!formData.project_id) {
                      toast({
                        title: 'Project Required',
                        description: 'Please select a project before adding activities',
                        variant: 'error'
                      });
                      return;
                    }
                    setEditingActivityIndex(null);
                    setIsActivityModalOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Activity
                </Button>
              </div>

              {activities.length > 0 ? (
                <div className="space-y-2">
                  {activities.map((activity, index) => (
                    <Card key={index} className="border">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-sm truncate">{activity.activity_description}</h4>
                              {activity.is_completed && (
                                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              {activity.location_area && <span>📍 {activity.location_area}</span>}
                              {activity.quantity_completed && (
                                <span>
                                  📊 {activity.quantity_completed} {activity.unit_of_measure}
                                </span>
                              )}
                              {activity.workers_assigned && <span>👷 {activity.workers_assigned} workers</span>}
                              {activity.start_time && (
                                <span>
                                  ⏰ {activity.start_time}
                                  {activity.end_time && ` - ${activity.end_time}`}
                                </span>
                              )}
                              {(activity.labour_cost || activity.materials_cost || activity.equipment_cost) && (
                                <span className="text-green-700 font-medium">
                                  💰 {(
                                    (parseFloat(activity.labour_cost) || 0) +
                                    (parseFloat(activity.materials_cost) || 0) +
                                    (parseFloat(activity.equipment_cost) || 0)
                                  ).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => handleEditActivity(index)}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteActivity(index)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-6 text-sm border border-dashed rounded-lg ${errors.activities ? 'border-destructive text-destructive' : 'text-muted-foreground'}`}>
                  {!formData.project_id
                    ? 'Select a project to add activities'
                    : errors.activities
                    ? 'At least one activity is required before saving'
                    : 'No activities added. Click "Add Activity" to get started.'}
                </div>
              )}
            </div>

            {/* Materials */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Package className="h-4 w-4" />
                Materials
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Delivered</Label>
                  <Textarea
                    value={formData.materials_delivered}
                    onChange={(e) => setFormData({ ...formData, materials_delivered: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Used</Label>
                  <Textarea
                    value={formData.materials_used}
                    onChange={(e) => setFormData({ ...formData, materials_used: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shortages</Label>
                  <Textarea
                    value={formData.material_shortages}
                    onChange={(e) => setFormData({ ...formData, material_shortages: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Safety & Quality */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Safety & Quality
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Safety Incidents</Label>
                  <Textarea
                    value={formData.safety_incidents}
                    onChange={(e) => setFormData({ ...formData, safety_incidents: e.target.value })}
                    placeholder="None or describe..."
                    rows={2}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quality Issues</Label>
                  <Textarea
                    value={formData.quality_issues}
                    onChange={(e) => setFormData({ ...formData, quality_issues: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="safety-meetings"
                  checked={formData.safety_meetings_held}
                  onChange={(e) => setFormData({ ...formData, safety_meetings_held: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="safety-meetings" className="cursor-pointer font-normal">
                  Safety meetings held today
                </Label>
              </div>
            </div>

            {/* Photos */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Photos ({photos.length})
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => document.getElementById('photo-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={photo.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded border"
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                        onClick={() => removePhoto(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Additional Notes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Problems</Label>
                  <Textarea
                    value={formData.problems_encountered}
                    onChange={(e) => setFormData({ ...formData, problems_encountered: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Corrective Actions</Label>
                  <Textarea
                    value={formData.corrective_actions}
                    onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Follow-up Required</Label>
                  <Textarea
                    value={formData.follow_up_required}
                    onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.value })}
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                if (onCancel) onCancel();
              }}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading} className="w-full sm:w-auto">
              <Save className="h-4 w-4 mr-2" />
              {isLoading ? 'Saving...' : mode === 'create' ? 'Create Log' : 'Update Log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Modal */}
      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={() => {
          setIsActivityModalOpen(false);
          setEditingActivityIndex(null);
        }}
        onSave={handleSaveActivity}
        activity={editingActivityIndex !== null ? activities[editingActivityIndex] : undefined}
        scheduledTasks={scheduledTasks}
        boqItems={boqItems}
        organizationId={organizationId}
      />
    </>
  );
}
